/**
 * NotificationInbox — the in-app alert inbox (UAT-R2-002 web half).
 * Lists the alerts RAISED for the signed-in manager (initial-report submits,
 * PPE FAIL/WARN inspections), newest first, with read/unread state and deep
 * links to the source. Honesty: a row is the record of an alert that was
 * RAISED for this user — it is never a delivery claim (no "delivered" wording);
 * "Read" only means "seen in the inbox".
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, ExternalLink } from 'lucide-react';
import { notificationApi, type InboxNotification } from '../utils/api';

function normalizeRows(res: any): InboxNotification[] {
  // Accepts both the real API body ({ success, data: [...] }) and axios-style
  // responses ({ data: { data: [...] } }) — the row list is what matters.
  const rows = res?.data?.data ?? res?.data ?? res;
  return Array.isArray(rows) ? rows : [];
}

export function NotificationInbox() {
  const [items, setItems] = useState<InboxNotification[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await notificationApi.getAll();
      setItems(normalizeRows(res));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not load your alerts.');
      setItems([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const markRead = async (id: number) => {
    try {
      await notificationApi.markRead(id);
      setItems((prev) => (prev ?? []).map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Could not mark the alert as read.');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 p-6 mb-6" data-testid="notification-inbox">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
        <Bell className="h-5 w-5 text-amber-600" />
        Alerts for you
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Manager alerts raised for your account — initial reports and PPE inspection failures. These are
        records of alerts that were raised, not delivery receipts.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 text-red-700">{error}</div>
      )}

      {items === null && !error && <p className="text-sm text-gray-500">Loading alerts…</p>}

      {items !== null && items.length === 0 && !error && (
        <p className="text-sm text-gray-600">
          No alerts yet — when a manager alert is raised for you it will appear here
        </p>
      )}

      {items !== null && items.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {items.map((n) => {
            const unread = !n.readAt;
            return (
              <li key={n.id} className={`py-3 flex items-start gap-3 ${unread ? 'bg-amber-50/40' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {n.link ? (
                      <Link
                        to={n.link}
                        onClick={() => { if (unread) void markRead(n.id); }}
                        className="font-medium text-gray-900 hover:underline truncate inline-flex items-center gap-1"
                      >
                        {n.title}
                        <ExternalLink className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      </Link>
                    ) : (
                      <span className="font-medium text-gray-900 truncate">{n.title}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">{n.body}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-400">{new Date(n.createdAt).toLocaleString()}</span>
                    <span className={`text-xs font-medium ${unread ? 'text-amber-700' : 'text-gray-400'}`}>
                      {unread ? 'Unread' : 'Read'}
                    </span>
                  </div>
                </div>
                {unread && (
                  <button
                    onClick={() => void markRead(n.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 shrink-0"
                  >
                    <Check className="h-3.5 w-3.5" /> Mark as read
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default NotificationInbox;
